import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { JobResult } from '../components/JobResult';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';

type UploadResult = { url: string; r2_key: string };

async function uploadImage(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/v1/face-swap/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${body}`);
  }
  return (await res.json()) as UploadResult;
}

function ImageInput({
  label,
  value,
  onChange,
  onClear,
  required,
}: {
  label: string;
  value: UploadResult | null;
  onChange: (v: UploadResult) => void;
  onClear: () => void;
  required?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>
      {value ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'action.hover' }}
        >
          <Box
            component="img"
            src={value.url}
            alt={label}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
          />
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} noWrap>
            {value.r2_key.split('/').slice(-1)[0]}
          </Typography>
          <IconButton size="small" onClick={onClear}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ) : (
        <Button
          component="label"
          variant="outlined"
          fullWidth
          startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          sx={{ justifyContent: 'flex-start', py: 2.25, borderStyle: 'dashed', color: 'text.secondary' }}
          disabled={busy}
        >
          {busy ? 'Uploading…' : 'Choose image'}
          <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handle} />
        </Button>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default function FaceSwap() {
  const t = useTranslation();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const [base, setBase] = useState<UploadResult | null>(null);
  const [face1, setFace1] = useState<UploadResult | null>(null);
  const [face2, setFace2] = useState<UploadResult | null>(null);
  const [face3, setFace3] = useState<UploadResult | null>(null);
  const [extraOpen, setExtraOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `remix` is accepted to match the other pages' history-retry contract, even
  // though face-swap has no scalar fields to pre-fill.
  void remix;

  const canSubmit = !!base && !!face1 && !submitting;
  const credits = usePriceQuote({ endpoint: 'face-swap' });

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    if (!base || !face1) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const res = await fetch('/api/v1/face-swap/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: base.url,
          image_r2_key: base.r2_key,
          second_image_url: face1.url,
          second_image_r2_key: face1.r2_key,
          third_image_url: face2?.url,
          third_image_r2_key: face2?.r2_key,
          fourth_image_url: face3?.url,
          fourth_image_r2_key: face3?.r2_key,
          fingerprint,
        }),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
    setBase(null);
    setFace1(null);
    setFace2(null);
    setFace3(null);
  };

  return (
    <Box>
      <PageHeader title={t.faceSwap.title} description={t.faceSwap.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>

      {activityId ? (
        <Section title={t.faceSwap.resultTitle}>
          <Paper sx={{ p: 3 }}>
            <JobResult
              activityId={activityId}
              resultType="image"
              onReset={reset}
              resetLabel={t.faceSwap.newGeneration}
            />
          </Paper>
        </Section>
      ) : (
        <>
          <Section title={t.faceSwap.imagesTitle} description={t.faceSwap.imagesDescription}>
            <Paper sx={{ p: 3 }}>
              <Stack spacing={3}>
                <ImageInput label={t.faceSwap.baseImage} value={base} onChange={setBase} onClear={() => setBase(null)} required />
                <ImageInput label={t.faceSwap.referenceFace1} value={face1} onChange={setFace1} onClear={() => setFace1(null)} required />
                <Box>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={extraOpen ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                    onClick={() => setExtraOpen((v) => !v)}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                  >
                    {extraOpen ? t.faceSwap.hideExtraFaces : t.faceSwap.addExtraFaces}
                  </Button>
                  <Collapse in={extraOpen}>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                      <ImageInput label={t.faceSwap.referenceFace2} value={face2} onChange={setFace2} onClear={() => setFace2(null)} />
                      <ImageInput label={t.faceSwap.referenceFace3} value={face3} onChange={setFace3} onClear={() => setFace3(null)} />
                    </Stack>
                  </Collapse>
                </Box>
              </Stack>
            </Paper>
          </Section>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                `${t.faceSwap.generate}${credits != null ? ` (${credits} ${t.credits.balanceUnit})` : ''}`
              )}
            </Button>
          </Stack>
        </>
      )}
      </Box>
      <RecentGenerationsStrip endpoint="face-swap" />
      </Box>
    </Box>
  );
}
